import { z } from 'zod';

import { isApiError } from 'utils/exceptions/apiError';

const couponNotEligibleRejectionSchema = z.object({
  error: z.literal('COUPON_NOT_ELIGIBLE'),
  couponVerdict: z.discriminatedUnion('status', [
    z.object({
      status: z.literal('date_range_valid'),
      couponCode: z.string(),
      expiryDate: z.string(),
    }),
    z.object({ status: z.literal('expired'), couponCode: z.string() }),
    z.object({ status: z.literal('not_found') }),
  ]),
});

export type CouponNotEligibleRejection = z.infer<
  typeof couponNotEligibleRejectionSchema
>;

/**
 * Parses the BFF's coupon rejection out of a failed Opportunity PATCH
 * (HTTP 409 `{ error: 'COUPON_NOT_ELIGIBLE', couponVerdict }`). Returns
 * `undefined` for anything else so callers fall through to generic handling.
 */
export function parseCouponNotEligibleRejection(
  error: unknown,
): CouponNotEligibleRejection | undefined {
  if (!isApiError(error) || error.status !== 409 || !error.body) {
    return undefined;
  }

  let body: unknown;
  try {
    body = JSON.parse(error.body);
  } catch {
    // Non-JSON 409 body — not the coupon rejection.
    return undefined;
  }

  const parsed = couponNotEligibleRejectionSchema.safeParse(body);

  return parsed.success ? parsed.data : undefined;
}
